import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError, BaseResponseEntities } from 'app/shared/reducers/reducer.utils';
import { IReportSchedule, defaultValueReportSchedule } from 'app/shared/model/report-schedule.model';
import { apiOkr } from 'app/config/axios-interceptor';

const initialState: EntityState<IReportSchedule> = {
  loading: false,
  errorMessage: null,
  entities: [],
  options: [],
  entity: defaultValueReportSchedule,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/okr/report-schedule';

// Actions

export const getEntities = createAsyncThunk(
  'reportSchedule/fetch_entity_list',
  async ({ searchText, page, size, sort, filters, year }: IQueryParams) => {
    return apiOkr.get<BaseResponseEntities<IReportSchedule>>(`${apiUrl}/pagination`, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        year: year || filters?.year,
        sort: sort && sort,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getAllReportSchedule = createAsyncThunk('reportSchedule/fetch_all_entity_list', async () => {
  return apiOkr.get<IReportSchedule[]>(`${apiUrl}`);
});

export const getByMonth = createAsyncThunk(
  'reportSchedule/fetch_entities_by_month',
  async (month: number) => {
    return apiOkr.get<IReportSchedule[]>(`${apiUrl}/by-month`, {
      params: {
        month,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'reportSchedule/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiOkr.get<IReportSchedule>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'reportSchedule/create_entity',
  async (entity: IReportSchedule, thunkAPI) => {
    const result = await apiOkr.post<IReportSchedule>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'reportSchedule/update_entity',
  async (entity: IReportSchedule, thunkAPI) => {
    const result = await apiOkr.put<IReportSchedule>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const enable = createAsyncThunk(
  'planPeriod/close_or_open_planPeriod',
  async (id: string, thunkAPI) => {
    const result = await apiOkr.put<IReportSchedule>(`${apiUrl}/${id}/enable`);
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'reportSchedule/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await apiOkr.delete<IReportSchedule>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const ReportScheduleSlice = createEntitySlice({
  name: 'reportSchedule',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getByMonth), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          options: data.map(x => {
            return {
              label: x.name,
              value: x.id,
            };
          }),
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.items,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isFulfilled(getAllReportSchedule), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getAllReportSchedule), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ReportScheduleSlice.actions;

// Reducer
export default ReportScheduleSlice.reducer;
